import * as Actions from '../actions/discountActions';
import Discount from '../../entities/discount';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type DiscountAction = { 
    type: string; 
    discount: Discount | null; 
    discountId: string | Discount; 
    partial: boolean; 
    lastFetch: number; 
    discounts: Discount[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Discount> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Discount[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Discount> | null, firmId: string, userId: string, updates?: Discount[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'discount', 'discounts', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Discount>} data
 * @param {Discount[]} [updates]
 * @returns {ArrayMap<Discount>}
 */
function updateStorage(data: ArrayMap<Discount>, updates?: Discount[]): ArrayMap<Discount> {
	if (logging) console.debug('⮂ discountReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Discount[]} [updates]
 * @returns {ArrayMap<Discount>}
 */
function updateStorageNoReturn(updates?: Discount[]): void {
	if (logging) console.debug('⮂ discountReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Discount[]} [data]
 * @returns {ArrayMap<Discount>}
 */
function createState(data?: Discount[] | ArrayMap<Discount>): ArrayMap<Discount> {
	if (!data) return ArrayMapRegistry.clone<Discount>('discount', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Discount>('discount', data.map(e => e));
    else return ArrayMapRegistry.clone<Discount>('discount', data);
}

function discounts(state: ArrayMap<Discount>, action: DiscountAction): ArrayMap<Discount> {
	if (!state) state = createState();

	let st: ArrayMap<Discount>;

	switch (action.type) {
		// action.discount - the new value of discount with id action.id
		// action.discountId - id of the target, used if new value is null
		case Actions.DISCOUNT_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ discountReducer: FETCH_ONE_SUCCESS', action);

			if (action.discount === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.discountId)) {
				st = state.clone();
				st.set(action.discount);
				// state.clear();

				updateStorageNoReturn([action.discount]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.discounts
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.discounts
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.discounts
			 !partial, !stateEmpty, fetchEmpty	-> D return discounts in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge discounts in state newer than those in action.discounts
		 */
		case Actions.DISCOUNT_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.discounts.length === 0;
			const fetched = action.discounts.map(discount => new Discount({...discount, persisted: true}));

			if (logging) console.debug(`⮂ discountReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} discounts with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ discountReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ discountReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Discount> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Discount[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(discount => {
					const current = st.get(discount.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && discount.status === 'D') {
							// Remove it from state
							toUpdate.push(discount);
							st.delete(discount);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (discount.addedOn > current.addedOn) {
								toUpdate.push(discount);
								st.set(discount);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (discount.status !== 'D') {
							toUpdate.push(discount);
							st.set(discount);
						}
						else if (logging) console.debug('⮂ discountReducer: partialMerge: skipping deleted discount not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ discountReducer: empty: discounts', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ discountReducer: fullMerge: discounts', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localDiscount => {
                    // If this discount has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localDiscount.persisted) {
                        const fetchedDiscount = st.get(localDiscount.id);
                        if (!fetchedDiscount || localDiscount.addedOn > fetchedDiscount.addedOn) st.set(localDiscount);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.DISCOUNT_FETCH_ONE_FAILURE:
			console.error('DISCOUNT_FETCH_ONE_FAILURE', action.discountId, action.err);

			// No update to storage required
			return state;

		case Actions.DISCOUNT_FETCH_FAILURE:
			console.error('DISCOUNT_FETCH_FAILURE', action.discountId, action.err);

			// No update to storage required
			return state;

		case Actions.DISCOUNT_RESTORE: {
		    let discount = new Discount({...action.discount});
            st = state.clone();

            const existing = st.get(discount.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (discount.addedOn >= existing.addedOn) {
                    st.set(discount);
                    return updateStorage(st, [discount]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (discount.status === 'D') return state;

                st.set(discount);
                return updateStorage(st, [discount]);
            }
        }
            
		case Actions.DISCOUNT_SAVE: {
		    // console.debug('⮂ DISCOUNT_SAVE reducer', action.discount?.id);
		    
			// clone the discount
			let discount = new Discount({...action.discount});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new discount in the state
			// If this is a create, add to the queue
			const _discount = st.get(discount.id);

			if (!_discount) st.set(discount);
			else {
				if (discount.addedOn >= _discount.addedOn) st.set(discount);
			}

			return updateStorage(st, [discount]);
		}

		case Actions.DISCOUNT_SAVE_MANY: {
		    if (logging) console.debug('⮂ DISCOUNT_SAVE_MANY reducer', action.discounts.map(discount => discount.id).join(','));
		    
			// clone the discounts
			let toProcess = action.discounts.map(discount => new Discount(discount));

			st = state.clone();
			// state.clear();

			const updates: Discount[] = [];

			toProcess.forEach(discount => {
				// Add or update the new discount in the state
				// If this is a create, add to the queue
				const existing = st.get(discount.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (discount.status !== 'D') {
						st.push(discount);
						updates.push(discount);
					}
					else if (logging) console.debug('⮂ DISCOUNT_SAVE_MANY: skipping deleted discount not in state');
				}
				else {
					if (discount.addedOn >= existing.addedOn) {
						st.set(discount);
						updates.push(discount);
					}
					else if (logging) console.debug('⮂ Skipping, existing discount had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.DISCOUNT_REMOVE: {
			// clone the discount
			const discount = new Discount({...action.discount});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(discount);

			return updateStorage(st, [discount]);
		}

		case Actions.DISCOUNT_RESTORE_FAILURE:
		case Actions.DISCOUNT_SAVE_FAILURE: {
		    console.warn('DISCOUNT_SAVE_FAILURE reducer', action.discount?.id);
		    if (!action.discount?.id) {
				console.warn('DISCOUNT_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const discount = ensure(Discount, action.discount);
			if (!discount) {
				console.error('Could not find discount after call to save');
				return state;
			}

			st = state.clone();

			const _discountRef = state.get(discount.id);

			if (_discountRef && (_discountRef.addedOn === discount.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _discount = new Discount({..._discountRef});
				_discount.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_discount.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('discount', _discount).then(r => {
						if (r.isErr()) console.error(`⮂ discountReducer: failed to save discount with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ discountReducer: saved discount with fatalError info');
					}).catch(err => console.error(`⮂ discountReducer: failed to save discount with fatalError info: ${err.message}`));

				st.set(_discount);
			}

			return updateStorage(st);
		}

		case Actions.DISCOUNT_SAVE_MANY_FAILURE: {
		    console.warn('DISCOUNT_SAVE_MANY_FAILURE reducer', action.discounts.map(discount => discount.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.DISCOUNT_RESTORE_SUCCESS:
		case Actions.DISCOUNT_SAVE_SUCCESS: {
			if (!action.discount?.id) {
				console.warn('DISCOUNT_SAVE_SUCCESS reducer');
                return state;
			}

			const discountRef = state.get(action.discount?.id);
			if (!discountRef) {
				console.warn('Could not find discount after call to save');
                return state;
			}

			st = state.clone();

			if (action.discount.addedOn >= discountRef.addedOn) {
				st.set(action.discount);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const discount = new Discount({...discountRef, persisted: true});
				st.set(discount);
			}

			return updateStorage(st, [action.discount]);
		}

		case Actions.DISCOUNT_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ DISCOUNT_SAVE_MANY_SUCCESS reducer', action.discounts.map(discount => discount.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const discounts: Discount[] = [];

			action.discounts.forEach(e => {
				const discountRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!discountRef) console.warn('Could not find discount after call to save');
				else if (e.addedOn >= discountRef.addedOn) {
				    st.set(e);
				    discounts.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const discount = new Discount({...discountRef, persisted: true});
					st.set(discount);
					discounts.push(discount);
				}
			});

			return updateStorage(st, discounts);
		}

		case Actions.DISCOUNT_REMOVE_FAILURE: {
		    console.warn('DISCOUNT_REMOVE_FAILURE action', action.discount?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.DISCOUNT_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.DISCOUNT_RESTORE_MANY: {
			if (logging) console.debug('⮂ DISCOUNT_RESTORE_MANY reducer', action.discounts.map(discount => discount.id).join(','));

			const toProcess = action.discounts.map(discount => new Discount({...discount}));
			st = state.clone();

			const updates: Discount[] = [];

			toProcess.forEach(discount => {
				const existing = st.get(discount.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (discount.addedOn >= existing.addedOn) {
						st.set(discount);
						updates.push(discount);
					}
					else if (logging) console.debug('⮂ DISCOUNT_RESTORE_MANY: skipping older version of discount');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (discount.status !== 'D') {
						st.set(discount);
						updates.push(discount);
					}
					else if (logging) console.debug('⮂ DISCOUNT_RESTORE_MANY: skipping deleted discount not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.DISCOUNT_REMOVE_MANY: {
			if (logging) console.debug('⮂ DISCOUNT_REMOVE_MANY reducer', action.discounts.map(discount => discount.id).join(','));

			const toProcess = action.discounts.map(discount => new Discount({...discount}));
			st = state.clone();

			toProcess.forEach(discount => {
				st.delete(discount);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default discounts;